﻿/**
 * Favorites Manager
 * App Proxy integration + login redirect + favorites page rendering.
 */

document.addEventListener("DOMContentLoaded", function () {
  const FAVORITE_PENDING_KEY = "toolbox_pending_favorite_toggle_v1";
  const DEFAULT_PROXY_BASE = "/apps/toolbox";
  const proxyBase = String(
    window.ToolboxFavoritesProxyBasePath || DEFAULT_PROXY_BASE,
  ).replace(/\/$/, "");

  const endpoints = {
    favorites: `${proxyBase}/favorites`,
    favoriteProducts: `${proxyBase}/favorites/products`,
  };

  let favoritesSet = new Set();
  let favoritesLoaded = false;
  const inFlightByProduct = new Set();
  const shopDomain = String(
    window.ToolboxShopDomain || window.Shopify?.shop || "",
  ).trim();
  const customerId = String(window.ToolboxCustomerId || "").trim();

  function isLoggedInCustomer() {
    return Boolean(window.ToolboxCustomerLoggedIn && customerId);
  }

  function withContextQuery(url) {
    const resolved = new URL(url, window.location.origin);
    if (shopDomain) {
      resolved.searchParams.set("shop", shopDomain);
    }
    if (customerId) {
      resolved.searchParams.set("customer_id", customerId);
    }
    return resolved.toString();
  }

  function getProductButtons(productId) {
    const id = String(productId || "").trim();
    if (!id) return [];

    return Array.from(
      document.querySelectorAll(".information-top-favorite[data-product-id]"),
    ).filter((button) => String(button.dataset.productId || "") === id);
  }

  function setFavoriteButtonState(button, isFavorited) {
    const productCard = button.closest(".collection-product-card");

    button.setAttribute("aria-pressed", String(isFavorited));
    button.classList.toggle("is-favorited", isFavorited);
    button.setAttribute(
      "aria-label",
      isFavorited ? "Remover dos favoritos" : "Favoritar curso",
    );
    button.setAttribute(
      "title",
      isFavorited ? "Remover dos favoritos" : "Favoritar curso",
    );

    if (productCard) {
      productCard.classList.toggle("is-favorited-card", isFavorited);
    }
  }

  function setFavoriteStateForProduct(productId, isFavorited) {
    getProductButtons(productId).forEach((button) => {
      setFavoriteButtonState(button, isFavorited);
    });
  }

  function syncButtonsFromFavoritesSet() {
    document
      .querySelectorAll(".information-top-favorite[data-product-id]")
      .forEach((button) => {
        const productId = String(button.dataset.productId || "").trim();
        if (!productId) return;
        setFavoriteButtonState(button, favoritesSet.has(productId));
      });
  }

  function updateFavoritesCountUI(count) {
    const safeCount = Number.isFinite(Number(count)) ? Number(count) : 0;

    document.querySelectorAll("[data-favorites-count]").forEach((node) => {
      node.textContent = String(safeCount);
    });

    document.querySelectorAll("[data-favorites-badge]").forEach((badge) => {
      badge.classList.toggle("hidden", safeCount <= 0);
    });
  }

  async function requestJson(url, options) {
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        ...(options?.headers || {}),
      },
      ...options,
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch (_err) {
      payload = null;
    }

    if (!response.ok || (payload && payload.success === false)) {
      const message = payload?.error || `Request failed (${response.status})`;
      const error = new Error(message);
      error.status = response.status;
      error.payload = payload;
      throw error;
    }

    return payload || {};
  }

  async function fetchFavorites() {
    const payload = await requestJson(withContextQuery(endpoints.favorites), {
      method: "GET",
    });

    const favorites = Array.isArray(payload.favorites)
      ? payload.favorites.map((id) => String(id || "").trim()).filter(Boolean)
      : [];

    favoritesSet = new Set(favorites);
    favoritesLoaded = true;
    syncButtonsFromFavoritesSet();
    updateFavoritesCountUI(payload.favoritesCount ?? favorites.length);

    return payload;
  }

  async function toggleFavorite(productId) {
    const payload = await requestJson(withContextQuery(endpoints.favorites), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action: "toggle",
        productId: String(productId),
        customerId,
        shop: shopDomain,
      }),
    });

    const favorites = Array.isArray(payload.favorites)
      ? payload.favorites.map((id) => String(id || "").trim()).filter(Boolean)
      : [];

    favoritesSet = new Set(favorites);
    favoritesLoaded = true;
    updateFavoritesCountUI(payload.favoritesCount ?? favorites.length);

    return payload;
  }

  function savePendingFavoriteToggle(productId) {
    try {
      sessionStorage.setItem(
        FAVORITE_PENDING_KEY,
        JSON.stringify({ action: "toggle", productId: String(productId) }),
      );
    } catch (_err) {}
  }

  function readPendingFavoriteToggle() {
    try {
      const raw = sessionStorage.getItem(FAVORITE_PENDING_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.action !== "toggle" || !parsed.productId) return null;
      return parsed;
    } catch (_err) {
      return null;
    }
  }

  function clearPendingFavoriteToggle() {
    try {
      sessionStorage.removeItem(FAVORITE_PENDING_KEY);
    } catch (_err) {}
  }

  function redirectToLoginWithReturnUrl() {
    const returnUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.assign(
      `/account/login?return_url=${encodeURIComponent(returnUrl || "/")}`,
    );
  }

  function productCardHtml(product) {
    const title = product?.title || "Produto";
    const url = product?.url || (product?.handle ? `/products/${product.handle}` : "#");
    const imageSrc = product?.image?.src || "";
    const imageAlt = product?.image?.alt || title;
    const priceAmount = product?.price?.amount;
    const priceCurrency = product?.price?.currency || product?.price?.currencyCode;
    const priceText =
      priceAmount && priceCurrency
        ? new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: priceCurrency,
          }).format(Number(priceAmount))
        : "";

    const imageHtml = imageSrc
      ? `<img src="${imageSrc}" alt="${imageAlt}" loading="lazy">`
      : `<div class="favorites-page__image-placeholder"></div>`;

    return `
      <article class="favorites-page__card" data-favorite-product="${product.id}">
        <a class="favorites-page__image-link" href="${url}">${imageHtml}</a>
        <div class="favorites-page__content">
          <a class="favorites-page__title" href="${url}">${title}</a>
          ${priceText ? `<div class="favorites-page__price">${priceText}</div>` : ""}
          <button type="button" class="favorites-page__remove" data-favorite-remove="${product.id}">Remover</button>
        </div>
      </article>
    `;
  }

  async function renderFavoritesPage() {
    const root = document.querySelector("[data-favorites-page]");
    if (!root) return;

    const listEl = root.querySelector("[data-favorites-list]");
    const emptyEl = root.querySelector("[data-favorites-empty]");
    const loadingEl = root.querySelector("[data-favorites-loading]");

    if (!listEl) return;

    if (loadingEl) loadingEl.hidden = false;
    if (emptyEl) emptyEl.hidden = true;
    listEl.innerHTML = "";

    if (!isLoggedInCustomer()) {
      redirectToLoginWithReturnUrl();
      return;
    }

    try {
      const payload = await requestJson(withContextQuery(endpoints.favoriteProducts), {
        method: "GET",
      });

      const products = Array.isArray(payload.products) ? payload.products : [];

      if (Array.isArray(payload.favorites)) {
        favoritesSet = new Set(
          payload.favorites.map((id) => String(id || "").trim()).filter(Boolean),
        );
      } else {
        favoritesSet = new Set(products.map((p) => String(p?.id || "").trim()).filter(Boolean));
      }

      favoritesLoaded = true;
      updateFavoritesCountUI(payload.favoritesCount ?? favoritesSet.size);
      syncButtonsFromFavoritesSet();

      if (!products.length) {
        if (emptyEl) emptyEl.hidden = false;
        return;
      }

      listEl.innerHTML = products.map(productCardHtml).join("");
    } catch (err) {
      console.error("Erro ao carregar pagina de favoritos:", err);
      if (emptyEl) {
        emptyEl.hidden = false;
        emptyEl.textContent = "Nao foi possivel carregar seus favoritos agora.";
      }
    } finally {
      if (loadingEl) loadingEl.hidden = true;
    }
  }

  async function replayPendingFavoriteToggleAfterLogin() {
    if (!isLoggedInCustomer()) return;

    const pending = readPendingFavoriteToggle();
    if (!pending) return;

    const productId = String(pending.productId || "").trim();
    if (!productId) {
      clearPendingFavoriteToggle();
      return;
    }

    try {
      await toggleFavorite(productId);
      setFavoriteStateForProduct(productId, favoritesSet.has(productId));
      clearPendingFavoriteToggle();
    } catch (err) {
      console.error("Erro ao processar favorito pendente:", err);
    }
  }

  async function initializeFavoritesForLoggedUser() {
    if (!isLoggedInCustomer()) return;

    try {
      await fetchFavorites();
    } catch (err) {
      console.error("Erro ao carregar favoritos:", err);
    }
  }

  document.addEventListener(
    "click",
    async function (event) {
      const favoriteButton = event.target.closest(".information-top-favorite");
      if (!favoriteButton) return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }

      const productId = String(favoriteButton.dataset.productId || "").trim();
      if (!productId) return;

      if (!isLoggedInCustomer()) {
        savePendingFavoriteToggle(productId);
        redirectToLoginWithReturnUrl();
        return;
      }

      if (inFlightByProduct.has(productId)) return;
      inFlightByProduct.add(productId);

      const currentState = favoriteButton.getAttribute("aria-pressed") === "true";
      const optimisticState = !currentState;
      setFavoriteStateForProduct(productId, optimisticState);

      try {
        const payload = await toggleFavorite(productId);
        const finalState =
          typeof payload.isFavorited === "boolean"
            ? payload.isFavorited
            : favoritesSet.has(productId);
        setFavoriteStateForProduct(productId, finalState);
      } catch (err) {
        console.error("Erro ao favoritar produto:", err);
        setFavoriteStateForProduct(productId, currentState);
      } finally {
        inFlightByProduct.delete(productId);
      }
    },
    true,
  );

  document.addEventListener("click", async function (event) {
    const removeButton = event.target.closest("[data-favorite-remove]");
    if (!removeButton) return;

    event.preventDefault();

    if (!isLoggedInCustomer()) {
      redirectToLoginWithReturnUrl();
      return;
    }

    const productId = String(removeButton.getAttribute("data-favorite-remove") || "").trim();
    if (!productId) return;
    if (inFlightByProduct.has(productId)) return;

    inFlightByProduct.add(productId);

    try {
      await toggleFavorite(productId);
      const card = removeButton.closest("[data-favorite-product]");
      if (card && !favoritesSet.has(productId)) {
        card.remove();
      }

      const pageRoot = document.querySelector("[data-favorites-page]");
      const listEl = pageRoot?.querySelector("[data-favorites-list]");
      const emptyEl = pageRoot?.querySelector("[data-favorites-empty]");
      if (listEl && !listEl.children.length && emptyEl) {
        emptyEl.hidden = false;
      }

      setFavoriteStateForProduct(productId, false);
    } catch (err) {
      console.error("Erro ao remover favorito na pagina:", err);
    } finally {
      inFlightByProduct.delete(productId);
    }
  });

  (async function init() {
    await replayPendingFavoriteToggleAfterLogin();
    if (!favoritesLoaded) {
      await initializeFavoritesForLoggedUser();
    }
    await renderFavoritesPage();
  })();
});
